import { Component } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * Global ErrorBoundary — catches any uncaught render/lifecycle error anywhere
 * beneath it and shows a friendly fallback instead of a blank black screen.
 * Logs the error + component stack to the console so we can debug from the
 * browser devtools.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Uncaught render error:", error, info?.componentStack);
    this.setState({ info });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, info: null });
    window.location.reload();
  };

  handleHome = () => {
    this.setState({ hasError: false, error: null, info: null });
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || "Unknown error";
    return (
      <div data-testid="error-boundary" className="min-h-screen bg-[#0A0605] text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full rounded-2xl border border-[#DC2626]/40 bg-[#180D0D] p-6 space-y-4">
          <div className="w-12 h-12 rounded-full bg-[#DC2626]/15 border border-[#DC2626]/40 flex items-center justify-center">
            <AlertTriangle size={22} className="text-[#DC2626]"/>
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">Something went wrong</h1>
            <p className="text-sm text-zinc-400 mt-1">We hit an unexpected error rendering this screen. Try reloading — if it keeps happening, drop us a note from the FAQ page.</p>
          </div>
          <pre data-testid="error-message" className="text-[11px] text-zinc-500 bg-black/40 rounded-lg p-3 overflow-x-auto max-h-32 whitespace-pre-wrap break-all">{msg}</pre>
          <div className="flex gap-2">
            <button
              data-testid="error-reload"
              onClick={this.handleReload}
              className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white text-sm font-semibold transition-colors"
            >
              <RefreshCw size={14}/> Reload
            </button>
            <button
              data-testid="error-home"
              onClick={this.handleHome}
              className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-full border border-zinc-700 bg-zinc-900 hover:border-[#DC2626]/50 text-white text-sm font-semibold transition-colors"
            >
              <Home size={14}/> Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
